import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
    Brand,
    Masthead,
    MastheadToggle,
    MastheadMain,
    MastheadBrand,
    MastheadContent,
    Page,
    PageToggleButton,
    PageSidebar,
    SkipToContent,
    Toolbar,
    ToolbarContent,
    ToolbarGroup,
    ToolbarItem,
    ToolbarToggleGroup,
} from '@patternfly/react-core';
import AppLogin from '@app/Auth/AppLogin';
import { BarsIcon } from '@patternfly/react-icons';
import { EllipsisVIcon } from '@patternfly/react-icons';
import { Link } from 'react-router-dom';
import logo from '@app/images/nda-logo.png';
import labLogo from '@app/images/labs-logo.png';
import UserAuthentication from "@app/Security/UserAuthentication";
import NavigationHelper from '@app/Service/NavigationHelper';
import Navigation from "@app/Components/Navigation/Navigation";
import ThemeStyle from "@app/Components/ThemeStyle/ThemeStyle";
import { UserContext } from '@app/Context/UserContext';
import { NavigationContext } from '@app/Context/NavigationContext';
import { NotificationContext } from '@app/Context/NotificationContext';
import { PageRouteContext } from '@app/Context/PageRouteContext';
import User from '@app/Interface/UserInterface';
import SelectFpu from '@app/Components/PageHeader/SelectFpu';
import FpuContextSelector from '@app/Components/PageHeader/FpuContextSelector';
import UserMenu from '@app/Components/PageHeader/UserMenu';
import ProducerInterface from '@app/Interface/ProducerInterface';
import NotificationHelper from '@app/Service/NotificationHelper';
import NotificationMenu from "@app/Components/PageHeader/NotificationMenu";
import PageRouteHelper from '@app/Service/PageRouteHelper';
import TemplateDefinitions from '@app/utils/TemplateDefinitions';
import ProducerHelper from '@app/Service/ProducerHelper';
import htmlParser from '@app/utils/htmlParser';
import PageLoader from '@app/Page/PageLoader';
import AppSettings from '@app/utils/AppSettings';
import ApiCache from '@app/Service/ApiCache';
import { useIdleTimer } from 'react-idle-timer';
import IdleWarningModal from '@app/Components/Modals/IdleWarningModal';
import IdleLogoutModal from '@app/Components/Modals/IdleLogoutModal';

interface IAppLayout {
    children: React.ReactNode;
}

const AppLayout: React.FunctionComponent<IAppLayout> = ({ children }) => {

    const [isLoading, setIsLoading] = React.useState(true);
    const [isNavOpen, setIsNavOpen] = React.useState(true);
    const [isMobileView, setIsMobileView] = React.useState(true);
    const [isNavOpenMobile, setIsNavOpenMobile] = React.useState(false);
    const [lastWindowSize, setLastWindowSize] = React.useState(0);
    const [isLoggedIn, setIsLoggedIn] = React.useState(false);
    const { userState, userDispatch } = React.useContext(UserContext);
    const { navigationState, navigationDispatch } = React.useContext(NavigationContext);
    const { notificationState, notificationDispatch } = React.useContext(NotificationContext);
    const { pageRouteDispatch, pageRouteState } = React.useContext(PageRouteContext);
    const notificationsPageRoute = PageRouteHelper.getPageRouteByTemplate(pageRouteState.pageRoutes, TemplateDefinitions.TEMPLATE_NOTIFICATIONS_PAGE);
    const canViewNotifications = UserAuthentication.canUserViewPage(userState.user, userState.user.currentFpuId, notificationsPageRoute.id);
    const paramFpu = Number(htmlParser.getParameterValueFromUrl("fpu", location.search));
    const history = useHistory();
    const [updateNavigationCacheInBackground, setUpdateNavigationCacheInBackground] = React.useState(false);
    const [displayIdleWarning, setDisplayIdleWarning] = React.useState(false);
    const [displayIdleLogoutModal, setDisplayIdleLogoutModal] = React.useState(false);
    const currentLocation = useLocation();

    // Used to log the user out, optionally show a modal on the login screen explaining they were logged
    // out due to idleness
    const logUserOut = (showLogoutModal: boolean = true) => {
        UserAuthentication.logout(true)
            .then((idleUserLoggedOut: boolean) => {
                if (idleUserLoggedOut) {
                    setIsLoggedIn(false);
                    setDisplayIdleWarning(false);
                    if (showLogoutModal) {
                        setDisplayIdleLogoutModal(true);
                    }
                } else {
                    console.log('Something went wrong with the logout');
                }
            });
    };

    // Function to call when the user should be warned that they will be idle soon
    const onPrompt = () => {
        // Prompt the user that they will be logged out for being idle
        setDisplayIdleWarning(true);
    };

    // Function to call when the user is considered idle
    const onIdle = () => {
        logUserOut();
    }

    // Idle timer, used to warn and eventually log a user out for idleness
    const idleTimer = useIdleTimer({
        timeout: AppSettings.getIdleWarningMilliseconds(),
        promptTimeout: AppSettings.getIdleLogoutMilliseconds(),
        startOnMount: false,
        startManually: true,
        debounce: 2000,
        onPrompt,
        onIdle
    });

    // called when fpu is changed in header.
    // relates to: DNDA-307 React component to select an FPU
    // converted to callback since onFpuChange is used in a useEffect() dependency. 
    //    prevents stale closure and calling the useEffect on every rerender. 
    //    suggested by eslint
    const onFpuChange = React.useCallback((fpu: ProducerInterface, redirect?: string) => {

        const updatedState = { ...userState };

        updatedState.user.currentFpuId = fpu.producerId;

        updatedState.user.currentFpu = fpu;

        userDispatch(
            {
                type: "UPDATE",
                payload: updatedState.user
            }
        );

        // redirect occurs when we want to remove querystring values from the url
        if (redirect) {
            history.replace(redirect);
        }

    }, [history, userDispatch, userState]);

    const onNavToggleMobile = () => {
        setIsNavOpenMobile(!isNavOpenMobile);
    };

    const onHandleLogin = (user: User) => {

        // check for remember fpu
        // this is updated whenever userDispatch updates the user.
        // see: src\app\Context\UserContext.tsx-> save state on every change
        const persistedProducerString = localStorage.getItem(AppSettings.REMEMBER_FPU_STORAGE_KEY);

        const persistedProducer: ProducerInterface | null = persistedProducerString ? JSON.parse(persistedProducerString) : null;

        // also make sure they have access to that PDU!
        // they could be logging in as a different user!
        if (persistedProducer && UserAuthentication.userHasFpu(user.viewPermissions, persistedProducer.producerId)) {
            user.currentFpuId = persistedProducer.producerId;
            user.currentFpu = persistedProducer;
        }

        // update the global user context
        userDispatch(
            {
                type: "ADD",
                payload: user
            }
        );

        // set logged in status depending on what the login result is.
        setIsLoggedIn(true);

    }

    const onNavToggle = () => {
        setIsNavOpen(!isNavOpen);
    }

    const onPageResize = (props: { mobileView: boolean; windowSize: number }) => {

        // if mobile view and the window actually resized, close the navigation
        if (props.mobileView && lastWindowSize !== props.windowSize) {
            setIsNavOpen(false);
            setIsNavOpenMobile(false);
        } else if (isMobileView && props.mobileView === false) {

            // if they were previously on the mobile view and widened the view
            // open the nav menu.
            setIsNavOpen(true);

        }

        setLastWindowSize(props.windowSize);
        setIsMobileView(props.mobileView);
    };

    // check for fpu id in the querystring
    // if present, select the fpu and reload the page to remove the querystring
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        const selectFpu = async (fpuId: number) => {

            const producers = await ProducerHelper.getProducers(controller, 1, String(fpuId));

            if (mounted && (producers).length === 1) {

                const producer = producers[0];

                const redirect = htmlParser.removeParameterFromUrl("fpu", location.pathname, location.search);

                onFpuChange(producer, redirect);

            }

        }

        if (isLoggedIn && paramFpu && userState.user.currentFpuId !== Number(paramFpu)) {

            const fpuId = Number(paramFpu);

            selectFpu(fpuId);
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [isLoggedIn, paramFpu, userState.user.currentFpuId, onFpuChange]);

    // get all the page routes  
    React.useEffect(() => {

        const getPageRoutes = async () => {

            const pageRoutes = await PageRouteHelper.getPageRoutes();

            if (pageRoutes) {

                pageRouteDispatch({
                    type: "UPDATE",
                    payload: pageRoutes
                });

            }

        };

        if (isLoggedIn) {
            getPageRoutes();
            idleTimer.start();
        } else {
            idleTimer.pause();
        }

    }, [isLoggedIn, pageRouteDispatch]);

    const HeaderToolbar = (
        <Toolbar id="nda-global-nav-toolbar" isStatic>
            <ToolbarContent>
                <ToolbarGroup alignment={{default: 'alignRight'}}>
                    <ToolbarToggleGroup breakpoint="md" toggleIcon={
                        <>
                            <span className="pf-u-mr-xs pf-u-font-weight-bold">Select FPU & More</span>
                            <EllipsisVIcon />
                        </>}>
                        <ToolbarItem 
                            spacer={{default: 'spacerNone'}} 
                            widths={{
                                default: '100%'
                            }}
                        >
                            {userState.user.systemRoles === false
                                ? <SelectFpu currentUser={userState.user} onFpuChange={onFpuChange} />
                                : <FpuContextSelector currentUser={userState.user} onFpuChange={onFpuChange} />
                            }
                        </ToolbarItem>
                        <ToolbarItem spacer={{default: 'spacerNone'}}>
                            {canViewNotifications && <NotificationMenu currentUser={userState.user} notifications={notificationState.notifications} onFpuChange={onFpuChange} />}
                        </ToolbarItem>
                        <ToolbarItem spacer={{default: 'spacerNone'}}>
                            <UserMenu currentUser={userState.user} />
                        </ToolbarItem>
                    </ToolbarToggleGroup>
                </ToolbarGroup>
            </ToolbarContent>
        </Toolbar>
    );

    const Header = (
        <Masthead display={{md: 'inline', sm: 'stack'}}>
            <MastheadToggle>
                <PageToggleButton 
                    variant="plain"
                    aria-label="Global navigation"
                    isNavOpen={isNavOpen}
                    onNavToggle={isMobileView ? onNavToggleMobile : onNavToggle}
                >
                    <BarsIcon />
                </PageToggleButton>
            </MastheadToggle>
            <MastheadMain>
                <MastheadBrand component={props => <Link {...props} to="/" />}>
                    <Brand
                        src={userState.user.isMember ? logo : labLogo}
                        alt={userState.user.isMember ? 'Northwest Dairy Association' : 'Northwest Labs'}
                        widths={{default: '150px'}}
                    >
                        <source src={userState.user.isMember ? logo : labLogo} />
                    </Brand>
                </MastheadBrand>
            </MastheadMain>
            <MastheadContent>
                {HeaderToolbar}
            </MastheadContent>
        </Masthead>
    );

    const NavigationRendered = (
        <Navigation fpuRoutes={navigationState.fpuRoutes}></Navigation>
    );

    const Sidebar = (
        <PageSidebar
            theme="dark"
            nav={NavigationRendered}
            isNavOpen={isMobileView ? isNavOpenMobile : isNavOpen} />
    );

    const PageSkipToContent = (
        <SkipToContent href="#primary-app-container">
            Skip to Content
        </SkipToContent>
    );

    // useEffect is called before Reacts render phase. 
    // Therefore, we are using it to check for login cookies 
    // and set logged in/out session in React.
    React.useEffect(() => {

        // no longer async
        const blnLoggedIn = UserAuthentication.userLoggedIn();

        if (blnLoggedIn) {

            // NOTE: UserContext.ts automatically loads user data from local storage!
            setIsLoggedIn(true);

        } else {

            // make sure the user context is empty. this clears any previous user data
            // to prevent showing stale data, especially if logging in with a different user.
            userDispatch({
                type: "LOGOUT"
            });

            // Just in case their session timed out. make sure to delete the navigation data.
            ApiCache.deleteNavigationData();

        }

        setIsLoading(false);

    }, [userDispatch]);

    // get the navigation based on current fpu
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function fetchRoutes() {

            if (userState.user.currentFpuId) {

                const userRoutes = await NavigationHelper.getAppRoutesForUser(controller, userState.user.currentFpuId);

                if (mounted && userRoutes) {

                    setUpdateNavigationCacheInBackground(true);

                    navigationDispatch(
                        {
                            type: "UPDATE",
                            payload: userRoutes
                        }
                    );

                }

            }

        }

        if (userState.user.currentFpuId) {
            fetchRoutes();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userState.user.currentFpuId]);

    // update navigation in background
    React.useEffect(() => {

        if (updateNavigationCacheInBackground) {

            NavigationHelper.getNavigationNonAsync(userState.user.currentFpuId, "");

            setUpdateNavigationCacheInBackground(false);

        }

    }, [updateNavigationCacheInBackground, userState.user.currentFpuId]);

    // get user notifications
    React.useEffect(() => {

        const controller = new AbortController();

        let mounted = true;

        async function getUserNotifications() {

            const notifications = await NotificationHelper.getNotifications(controller);

            if (mounted) {
                notificationDispatch(
                    {
                        type: "UPDATE",
                        payload: notifications
                    }
                );
            }

        }

        if (userState.user.id) {
            getUserNotifications();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [userState.user.id, notificationDispatch]);

    React.useEffect(() => {
        document.title = process.env.NDA_TITLE ?? "Northwest Dairy Association";
    }, []);

    // if new page, scroll to top
    React.useEffect(() => {

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });

        // if mobile, be sure to close the Navigation
        if (isMobileView) {
            setIsNavOpenMobile(false);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentLocation.pathname]);

    if (isLoading) {

        return (
            <>
                <ThemeStyle></ThemeStyle>
                <PageLoader />
            </>
        );

    }

    return (
        // check if user is logged in...
        !isLoggedIn ?
            // ...not logged in, serve up login form.
            <>
                <ThemeStyle></ThemeStyle>
                <AppLogin handleLogin={onHandleLogin} />
                <IdleLogoutModal 
                    isOpen={displayIdleLogoutModal}
                    onClose={() => {
                        setDisplayIdleLogoutModal(false);
                    }}
                />
            </>
            :
            // ...else, user is logged in, serve up page content.
            <>
                <ThemeStyle currentUser={userState.user}></ThemeStyle>
                <Page
                    mainContainerId="primary-app-container"
                    header={Header}
                    sidebar={Sidebar}
                    onPageResize={onPageResize}
                    skipToContent={PageSkipToContent}
                    className="ndaPageClass">
                    {children}
                </Page>
                <IdleWarningModal 
                    isOpen={displayIdleWarning}
                    idleTimer={idleTimer}
                    onLogOut={() => logUserOut(false)}
                    onUserInteraction={() => {
                        // Restart idle timer
                        idleTimer.start();
                        // Hide warning modal
                        setDisplayIdleWarning(false);
                    }}
                />
            </>
    );

}

export { AppLayout };
